import { useEffect, useMemo, useRef, useState } from "react";
import { Card, ErrorBanner, Pill, fmt } from "../components/ui";
import { api } from "../lib/api";

type T = Awaited<ReturnType<typeof api.redialTraffic>>;

/*
 * WRITTEN THREE TIMES. Each rewrite was somebody reading it and not understanding it.
 *
 *   1. Tables in the engine's own words - entry_type, actor_system, held_window. Ammar,
 *      2026-10-06: "does not make sense to me and i don't understand any damn thing about it".
 *   2. Same numbers in English, but fourteen panels deep. "your console is shitty right now and
 *      confusing".
 *   3. This one, structured from a reviewing agent's findings rather than my own eye.
 *
 * THE SHAPE, and why. Three questions in the order somebody actually asks them:
 *
 *      IS IT WORKING?      one sentence, plus a heartbeat - because "ON" only means a database
 *                          flag is true, and an engine that crashed at 06:00 reads ON all day.
 *      WHAT HAPPENS NEXT?  today, hour by hour, both halves: what has gone out and what is to come.
 *      WHAT HAPPENED?      the past, over one date range that every panel below obeys.
 *
 * The review also cut four panels. "In the dialler right now" is empty in the healthy state and an
 * empty card reads as a broken one, so it appears only when somebody IS in there. The funnel and
 * the adds/removes ledger are history, so they moved under the date range where their heading can
 * say which days they cover. The cadence-rules table belongs on the settings page, not here.
 */

/** `dial_no` counts STEPS in the cadence, whatever kind they are - so a plan whose first step was a
 *  text reaches its first call at step 2. "Step 2" is honest where "2nd call" is not. */
const ordinal = (n: number) =>
  n === 1 ? "step 1" : n === 2 ? "step 2" : n === 3 ? "step 3" : `step ${n}`;

/** Eastern calendar day, for the date inputs' own `max`. */
const etDay = (offset = 0) => {
  const d = new Date(Date.now() + offset * 86400000);
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
};

const dayLabel = (d: string) =>
  new Date(String(d).slice(0, 10) + "T12:00:00Z").toLocaleDateString("en-US", {
    timeZone: "America/New_York",
    weekday: "short",
    month: "short",
    day: "numeric",
  });

/** 11:00 → "11am", 13:00 → "1pm". An hour label needs no date; everything else gets `fmt.et`. */
const hourLabel = (h: number) =>
  h === 0 ? "12am" : h < 12 ? `${h}am` : h === 12 ? "12pm" : `${h - 12}pm`;

/*
 * `max_in_flight_redials` is NULLABLE and null means NO LIMIT. The previous version wrote
 * `?? 0`, which printed "Only 0 people sit in Five9 at a time" - the exact opposite of the truth -
 * while the settings table four panels down correctly said "no limit" for the same field.
 * One helper so the two can never disagree again.
 */
const limitPhrase = (n: number | null | undefined) =>
  n == null
    ? // The whole sentence has to change, not just the phrase. "There is no limit on how many sit
      // in Five9 at once, so a big hour drains in order rather than all ringing at once" - the
      // clause after the comma only holds when there IS a limit.
      "There is no limit set on how many sit in Five9 at once, so a big hour is handed over as fast as the engine can place it."
    : `Only ${fmt.n(n)} ${n === 1 ? "person sits" : "people sit"} in Five9 at a time, so a big hour drains in order rather than all ringing at once.`;

//: Why this person is in a calling queue at all.
const WHY_CALLING: Record<string, string> = {
  wants_call: "They asked us to call them",
  shoved: "We decided to call them",
  appointment: "They have an appointment booked",
  welcome_call: "New customer — welcome call",
};

//: WHICH SYSTEM put them in the queue. The main question this page exists to answer, because
//: three different systems can add somebody to the same Five9 list.
const WHO_ASKED: Record<string, string> = {
  call_now: "TextersAI — someone asked for a call",
  appointment: "Appointment booked",
  five9_list_takeover: "One-off import (Sept 30)",
  revive_stranded_callnow: "One-off repair of stuck call-nows",
  harness_test: "Our own testing",
};

/*
 * `list_ledger.actor_system` values, in English. Anything NOT in here renders with its raw name and
 * an "unrecognised" pill rather than being quietly prettified: a writer nobody expected is the
 * single most important thing this panel can tell you, and a Title-Cased unknown looks official.
 * Measured against production 2026-10-06: only `redial_engine` and `connected_sweep` appear so far.
 */
const SYSTEM_NAMES: Record<string, string> = {
  redial_engine: "This redial engine",
  connected_sweep: "Connected-call sweep — pulling out people we spoke to",
  connected_followup: "Next-day follow-up after a real conversation",
  textersai: "TextersAI — someone asked for a call",
  appointment: "Appointment automation",
  tracker: "Tracker (DASHBOARD) — booking cleanup",
  five9_takeover: "One-off import (Sept 30)",
  harness_test: "Our own testing",
};

//: The engine's reasons in English. Keys are the labels `traffic.HOLD_REASONS` produces.
const REASON_IN_ENGLISH: Record<string, string> = {
  "held: window closed": "Too early or too late in the day to call",
  "held: appointment booked": "They already have an appointment coming up",
  "held: already live in another list": "Already queued elsewhere — would have called twice",
  "held: daily redial limit": "Already called them once today",
  "held: temporary pause": "They just replied — giving them a moment",
  "held: high filter": "Quiet hours for this type of lead",
  "stopped: already spoke to them": "We had already had a real conversation with them",
  "stopped: consent / DND / replied": "Opted out, do-not-call, or they replied",
  "stopped: their phone had already rung enough today":
    "Already called them the maximum times for one day",
  "refused: GHL DND": "Marked do-not-call in GHL",
  "retired: too stale": "Too old to be worth calling now",
  "cancelled: plan ended": "Their calling sequence had already finished",
  "released: plan ended": "Sequence ended, so we took them out of the list",
  "cancelled: superseded": "Replaced by a newer sequence",
  "no text in this cadence": "This sequence does not send a text here",
  "drained: engine was off": "Engine was off — we removed them from the list",
  "NOT REMOVED - may still be dialable": "STILL IN A LIST — could be called again. Needs checking",
  "add failed": "Five9 refused to add them",
  // Amber on purpose, and NOT in `FINE`: a transient add failure is a real fault worth seeing.
  // It just is not a lost person any more - the engine will try again.
  "add failed - will try again": "Could not reach Five9 — trying them again shortly",
  "add failed - gave up after retrying": "Could not reach Five9 after several tries — NOT called",
  "revived: put back after the go-live drop": "Put back after being dropped at go-live",
  "add failed after a 603": "Five9 refused to re-add them",
  "re-added after a 603": "Already in the list, so we removed and re-added them",
  "unresolved: no disposition": "Five9 never told us how the call ended",
  "text not sent": "The text did not go out",
  "dry run - nothing really happened": "Test mode — nothing actually happened",
  "pulled back by hand": "We removed them by hand",
  // Hand actions. These were in the backend and NOT here, so the biggest row on the page - a
  // 963-attempt backfill - printed as raw developer text in amber. A test now asserts every label
  // `HOLD_REASONS` can emit has an entry in this map, because the two tables are in two languages
  // and nothing else can check across that gap.
  "retired by hand: stale at go-live":
    "Too old to call when the engine went live — dropped on purpose",
  "stopped by hand: consent remediation": "Stopped by hand while we checked their consent",
  "de-duplicated: was live in two lists": "Was queued twice — we kept one and removed the other",
  "moved to another list": "Moved to a different calling list",
  "stopped by hand: customer, not a lead": "Already a customer — taken off the sales cadence",
  // Our own caller ID, resolved as a "lead" from inverted Five9 dial data and caught before any
  // dial. Green: this is the guard working, not a fault.
  "stopped: that number is one of ours, not a lead":
    "That number is one of OUR Five9 phone numbers, not a customer — we did not call it",
  "cleaned up by hand: duplicate test plan": "A duplicate test record we removed",
  "cleaned up by hand: after a verification run": "Tidied up after a check we ran",
  "cancelled: duplicate of a plan they already had": "They were already on this sequence",
  "(none)": "Just waiting for their turn",
  other: "Something we have not labelled yet",
};

//: Green: the engine deliberately did not call somebody. That is it working, not failing.
const FINE = new Set([
  "held: window closed",
  "held: appointment booked",
  "held: already live in another list",
  "held: daily redial limit",
  "held: temporary pause",
  "held: high filter",
  "stopped: already spoke to them",
  "stopped: consent / DND / replied",
  "stopped: their phone had already rung enough today",
  "refused: GHL DND",
  "retired: too stale",
  "cancelled: plan ended",
  "released: plan ended",
  "cancelled: superseded",
  "no text in this cadence",
  "drained: engine was off",
  // A HUMAN decision is not the engine misbehaving. These fell through to amber, so an operator's
  // own actions looked like faults - most visibly the 963-row go-live backfill.
  "retired by hand: stale at go-live",
  "stopped by hand: consent remediation",
  "stopped by hand: customer, not a lead",
  "stopped: that number is one of ours, not a lead",
  "cleaned up by hand: duplicate test plan",
  "cleaned up by hand: after a verification run",
  "cancelled: duplicate of a plan they already had",
  "de-duplicated: was live in two lists",
  "moved to another list",
  "pulled back by hand",
]);

//: Red: somebody may still be sitting in a Five9 list and could be called again by mistake.
const NEEDS_ATTENTION = new Set(["NOT REMOVED - may still be dialable"]);

const RANGES = [
  { days: 1, label: "Today" },
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

/** Horizontal bar. `.viz-meter` is the TRACK - near-white - and the coloured bar is a `.seg`
 *  child. Setting the width on the track itself drew white on white, which is how the first
 *  version of this page shipped a chart nobody could see. */
function Bar({ value, peak, tone }: { value: number; peak: number; tone: string }) {
  if (value <= 0) return <span className="dim">—</span>;
  return (
    <div className="viz-meter" style={{ height: 10 }}>
      <div
        className="seg"
        style={{
          width: `${Math.max(2, Math.round((100 * value) / Math.max(1, peak)))}%`,
          background: tone,
        }}
      />
    </div>
  );
}

export function RedialTrafficPage() {
  const [t, setT] = useState<T | null>(null);
  const [f9, setF9] = useState<Awaited<ReturnType<typeof api.redialFive9State>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  //: Shown next to the range buttons, so a slow fetch is distinguishable from a dead control. The
  //: old data stays on screen meanwhile: the "right now" half does not depend on the range at all,
  //: so blanking the whole page to reload a past panel would be a worse trade.
  const [busy, setBusy] = useState(false);
  const reqId = useRef(0);

  // ── ONE date range for the whole page. There used to be two identical dropdowns in two
  //    different cards, each re-fetching everything, so changing one silently changed the other
  //    card's heading too. The server resolves whatever we send and echoes back the range it
  //    actually measured, which is what the headings print.
  const [days, setDays] = useState(7);
  const [custom, setCustom] = useState(false);
  const [from, setFrom] = useState(etDay(-6));
  const [to, setTo] = useState(etDay());

  /*
   * A REQUEST ID, not an in-flight flag.
   *
   * The flag version dropped the fetch entirely if the 20-second poll happened to be mid-flight,
   * and nothing re-scheduled it - so clicking "30 days" turned the button blue and then showed the
   * old window's numbers, under the old window's heading, for up to twenty seconds. A range control
   * that silently does nothing is worse than a slow one.
   *
   * Now every change fires, and a response that is not the newest is discarded.
   */
  useEffect(() => {
    // Both dates must be filled before asking. An empty `from` used to fall through to `days=7`
    // while the "Dates…" button stayed highlighted, so the page quietly showed a week.
    /*
     * The guard must NOT be an early `return` from the effect: that skipped the `setInterval`
     * registration too, so clearing one date input stopped the 20-second poll entirely. One payload
     * feeds both halves of this page, so the LIVE half - the headline, the in-flight list, today's
     * hours and the out-of-window alarm - silently froze while the caption still said "refreshing
     * every 20 seconds". An incident would not have appeared on screen.
     *
     * So: skip only the fetch, and keep the timer.
     */
    const ready = custom && Boolean(from && to);
    const load = () => {
      const started = ++reqId.current;
      setBusy(true);
      // A half-filled custom range falls back to the PRESET, and keeps fetching. Not fetching at
      // all would freeze the live half of the page, which does not depend on the range in the first
      // place; and the server echoes back the window it measured, so the headings stay honest about
      // which range is actually on screen.
      (ready ? api.redialTraffic({ from, to }) : api.redialTraffic({ days }))
        .then((x) => {
          if (started !== reqId.current) return;        // a newer request has been issued
          setT(x);
          setError(null);
        })
        .catch((e) => {
          if (started === reqId.current) setError(String(e?.message ?? e));
        })
        .finally(() => {
          if (started === reqId.current) setBusy(false);
        });
    };
    load();
    const id = setInterval(() => {
      if (!document.hidden) load();
    }, 20000);
    const wake = () => {
      if (!document.hidden) load();
    };
    document.addEventListener("visibilitychange", wake);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", wake);
    };
  }, [days, custom, from, to]);

  /*
   * Five9's own numbers, on their OWN timer. Six SOAP round trips, cached 90s on the server, and
   * they change slowly - polling them at the page's 20-second rate would be three wasted trips a
   * minute. A failure here must never blank the panel into "all lists empty", so the error is
   * rendered instead of the rows.
   */
  useEffect(() => {
    const load = () => api.redialFive9State().then(setF9).catch(() => {});
    load();
    const id = setInterval(() => {
      if (!document.hidden) load();
    }, 60000);
    return () => clearInterval(id);
  }, []);

  const holdRows = useMemo(() => {
    /*
     * ONE ROW PER REASON. The backend groups by (reason, attempt status), so "(none)" arrived
     * twice - once for people genuinely waiting their turn and once for attempts cancelled with no
     * reason recorded. Ammar saw two identical "Just waiting for their turn" rows, 2026-10-06.
     *
     * They are different facts, so they get different labels rather than being summed.
     */
    const merged = new Map<string, { label: string; n: number; tone: string; raw: string }>();
    for (const h of t?.holds_window ?? []) {
      /*
       * `kind` comes from the backend, which knows what the row actually is:
       *
       *   completed  the call went all the way through. `last_error` is never cleared, so a
       *              successful attempt ends with a NULL reason - and this card used to label 171
       *              of those "Ended, with no reason recorded" in AMBER, under a caption reading
       *              "Green is good. Red needs checking". Successful calls presented as anomalies.
       *   waiting    still scheduled, nothing has happened yet. Not a hold, and in the FUTURE, so
       *              it does not belong in a panel about what already happened.
       *   hold       the engine actually declined to call somebody. The only kind this card is for.
       */
      if (h.kind !== "hold") continue;
      const label = REASON_IN_ENGLISH[h.reason] ?? h.reason;
      const tone = NEEDS_ATTENTION.has(h.reason) ? "ba" : FINE.has(h.reason) ? "ok" : "wa";
      const prev = merged.get(label);
      merged.set(label, { label, tone, raw: h.reason, n: (prev?.n ?? 0) + h.n });
    }
    return [...merged.values()].sort((a, b) => b.n - a.n);
  }, [t]);

  if (!t)
    return (
      <>
        <ErrorBanner error={error} />
        {!error && <div className="empty">Reading the calling queue…</div>}
      </>
    );

  const q = t.queue ?? ({} as T["queue"]);
  const d = t.dials_today?.[0] ?? { confirmed_dials: 0, handed_to_five9: 0, people: 0 };
  const s = t.settings ?? ({} as T["settings"]);

  /*
   * A FAILED settings QUERY IS NOT "OFF". `_rows` swallows a query failure into [], and the
   * settings panel then degrades to {} - so `s.enabled` is undefined, which is falsy, and the page
   * announced "The redial engine is switched OFF. It is not calling anybody." about a live engine
   * dialling real people. Unknown and off must never look the same.
   */
  const switchesUnreadable = s.enabled === undefined;

  /*
   * THE SAME TRAP AS THE SWITCHES, on the panel that drives the headline.
   *
   * `_rows` swallows a failed query into [], so `queue` degrades to {} and every `?? 0` below
   * turns "I could not read this" into a confident zero - the headline then announces
   * "Nothing is waiting to be called." about a live queue of 1,144 people. That is precisely the
   * 2026-10-06 failure: an incident in progress and a screen that looks calm.
   */
  const queueUnreadable = q.scheduled === undefined;
  const dialsToday = t.dials_today?.[0];
  const dialsUnreadable = dialsToday === undefined;
  //: "—" rather than 0 wherever a number could not be read.
  const n = (v: number | undefined, unreadable: boolean) =>
    unreadable || v === undefined ? "—" : fmt.n(v);

  /*
   * THREE switches, not one. `dialer_enabled` is a config switch of its own, and leaving it out
   * made the page say "Calling now" while the dialler was off - `api.ts` even carries a comment
   * warning that conflating them "tells somebody people are being called when they are not".
   */
  const on = Boolean(s.enabled && !s.dry_run && s.dialer_enabled);
  const overdue = q.overdue ?? 0;
  /*
   * `in_flight` is `added` PLUS `claimed`, and they are not the same thing: 'claimed' means a
   * worker locked the row and has not called Five9 yet, so those people are NOT in the dialler -
   * after a worker crash they sit claimed until the lock expires. Saying "sitting in the dialler
   * waiting for Five9 to ring them" about a claimed row is simply false.
   */
  //: From the QUEUE COUNTS, not from `in_flight_detail` - that is `limit 50`, so deriving the
  //: headline from it said "50 people are sitting in the dialler" with 120 in flight.
  const detail = t.in_flight_detail ?? [];
  const reallyAdded = q.in_five9 ?? 0;
  const claimed = q.being_handed_over ?? 0;
  const inList = q.in_flight ?? 0;

  /*
   * NOT date-filtered. This read `holds_today`, so an unverified removal from yesterday - a person
   * genuinely still sitting in a live list, who WILL be dialled again - disappeared from the banner
   * and the tile at midnight while the record was still there. `Redialing.tsx` already tiles the
   * same fact with no date filter.
   */
  const unremoved = t.removal_unverified?.[0] ?? { n: 0, people: 0, oldest_at: null };
  const problemCount = unremoved.people || unremoved.n;
  const outOfWindow = t.out_of_window_today ?? [];
  const outOfWindowPeople = outOfWindow.reduce((a, r) => a + r.people, 0);

  /*
   * The dial window, computed by the BACKEND from the policies governing the people actually
   * queued - not hardcoded, and not "only when every policy agrees".
   *
   * That second rule was my own fix and it silently killed the feature: the live estate has four
   * policies at 11:00-20:45 and one test policy at 00:00-23:59, so they never agreed, `windowText`
   * was always null, and the "held until calling opens" sentence could never appear - which is the
   * sentence that matters at 09:00 on a go-live morning.
   */
  const dw = t.dial_window?.[0];
  const windowText = dw
    ? { open: dw.window_open.slice(0, 5), close: dw.window_close.slice(0, 5), others: dw.others }
    : null;

  /** Is the dial window shut right now, in Eastern time? Only answerable when all policies agree. */
  const windowShut = (() => {
    if (!windowText) return false;
    const nowEt = new Intl.DateTimeFormat("en-GB", {
      timeZone: "America/New_York",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(new Date());
    return nowEt < windowText.open || nowEt > windowText.close;
  })();

  // ── the heartbeat. "ON" is a flag; this is whether it is actually running. ─────
  const since = s.seconds_since_tick;
  const beat =
    s.last_tick_at == null
      ? { tone: "ba", text: "The engine has never reported in — it may not be running at all." }
      : since != null && since > 600
        ? {
            tone: "ba",
            text: `The engine has not run for ${
              since > 7200 ? `${Math.round(since / 3600)} hours` : `${Math.round(since / 60)} min`
            } — it should run every minute.`,
          }
        : since != null && since > 180
          ? { tone: "wa", text: `Last run ${Math.round(since / 60)} min ago — expected every minute.` }
          : { tone: "ok", text: `Running — last checked ${since ?? 0} seconds ago.` };

  /*
   * THE SENTENCE. What is happening, right now, in plain English.
   *
   * Order matters and it is not arbitrary:
   *   1. we cannot read the switches  - say so; never guess "off"
   *   2. the engine is not RUNNING    - an ON flag with a dead tick is not "calling now". This
   *                                     branch used to be missing entirely, so the page printed
   *                                     "Calling now. 41 people are sitting in the dialler" in
   *                                     15px type with "has not run for 9 hours" in 12px grey
   *                                     underneath it.
   *   3. the switches                 - off / test / dialler off
   *   4. what it is actually doing
   */
  let headline: string;
  if (switchesUnreadable) {
    headline = "Cannot read the engine's switches — this page does not know whether it is calling.";
  } else if (queueUnreadable) {
    // Before this branch existed, a failed queue query produced "Nothing is waiting to be called."
    headline = "Cannot read the calling queue — the numbers below are unknown, not zero.";
  } else if (s.enabled && !s.dry_run && beat.tone === "ba") {
    headline = `The engine is switched ON but ${
      s.last_tick_at == null ? "has never run" : "is not running"
    } — nothing is being called. ${beat.text}`;
  } else if (!s.enabled) {
    headline = "The redial engine is switched OFF. It is not calling anybody.";
  } else if (s.dry_run) {
    headline = "The engine is in TEST MODE — deciding what it would do, but never actually calling.";
  } else if (!s.dialer_enabled) {
    headline =
      "The engine is on but the DIALLER IS SWITCHED OFF, so it is scheduling calls and placing none.";
  } else if (inList > 0) {
    headline =
      reallyAdded > 0
        ? `Calling now. ${fmt.n(reallyAdded)} ${reallyAdded === 1 ? "person is" : "people are"} sitting in the dialler waiting for Five9 to ring them${claimed > 0 ? `, and ${fmt.n(claimed)} more are being handed over` : ""}.`
        : `${fmt.n(claimed)} ${claimed === 1 ? "person is" : "people are"} being handed to Five9 right now.`;
  } else if ((q.due_now ?? 0) > 0) {
    //: "working through them" is FALSE while the window is shut - the engine is deliberately
    //: holding them. Before 11:00 with an overnight backlog this read "833 people are due a call
    //: and the engine is working through them", which is the opposite of what it was doing.
    headline = windowShut
      ? `${fmt.n(q.due_now)} people are due a call and are being held until calling opens at ${windowText!.open} ET.`
      : `${fmt.n(q.due_now)} people are due a call and the engine is working through them.`;
  } else if (q.next_at) {
    headline = `Nothing to call at the moment. The next call is due ${fmt.et(q.next_at)} ET.`;
  } else {
    headline = "Nothing is waiting to be called.";
  }

  const win = t.window;
  const rangeLabel = !win
    ? `last ${t.window_days} days`
    : win.from === win.to
      ? win.from === etDay()
        ? "today"
        : dayLabel(win.from)
      : `${dayLabel(win.from)} – ${dayLabel(win.to)}`;

  const todayRows = t.schedule_today ?? [];
  const peakHour = Math.max(1, ...todayRows.map((h) => h.to_go + h.went_out));
  const toGoToday = todayRows.reduce((a, h) => a + h.to_go, 0);
  const wentToday = todayRows.reduce((a, h) => a + h.went_out, 0);

  return (
    <>
      <ErrorBanner error={error} />

      {/* ════════════ 1. IS IT WORKING ════════════ */}
      <div className="row w" style={{ marginBottom: 4 }}>
        <h2 style={{ fontSize: 19 }}>What the redial engine is doing</h2>
        {/* `!s.enabled` FIRST, mirroring the headline. Checking `dry_run` first made the pill read
            "TEST MODE" beside a sentence reading "switched OFF" - which is the shipped default,
            `enabled` false and `dry_run` true, so the two contradicted each other out of the box. */}
        {switchesUnreadable ? (
          <Pill tone="ba">switches unreadable</Pill>
        ) : (
          <Pill tone={on ? "ok" : s.enabled ? "wa" : "ba"}>
            {!s.enabled ? "OFF" : s.dry_run ? "TEST MODE" : !s.dialer_enabled ? "DIALLER OFF" : "ON"}
          </Pill>
        )}
        {/* Three states, not two. The amber branch says "last run 4 min ago" - so a pill reading
            "not running" beside it asserted the opposite of its own explanation. */}
        <Pill tone={beat.tone}>
          {beat.tone === "ok" ? "running" : beat.tone === "wa" ? "running late" : "not running"}
        </Pill>
      </div>

      <p style={{ marginTop: -2, fontSize: 15 }}>{headline}</p>
      <p className="dim" style={{ marginTop: 2, fontSize: 12 }}>
        {beat.text} · page updated {fmt.et(t.at)} ET, refreshing every 20 seconds
        {windowText
          ? ` · calls go out between ${windowText.open} and ${windowText.close} Eastern${
              // Never state one window as if it governed everybody when it does not. One test
              // record on a 00:00-23:59 cadence is exactly this case today.
              windowText.others > 0 ? ` (${fmt.n(windowText.others)} on a different schedule)` : ""
            }`
          : null}
      </p>

      {/*
        THE ALARM THIS PAGE WAS BUILT FOR, and the thing the previous version got wrong.
        On 2026-10-06 a 04:50 tick handed 38 real leads to a live list six hours early. The first
        version of this page did show the hour - as a row reading "4am · went out 38" with a GREEN
        bar - and left the owner to notice "4am" himself and know the window by heart. It now says
        so, in red, at the top, before anything else.
      */}
      {outOfWindow.length > 0 && (
        <div className="banner bad" style={{ marginTop: 10 }}>
          <b>Called outside the allowed hours:</b> {fmt.n(outOfWindowPeople)}{" "}
          {outOfWindowPeople === 1 ? "person was" : "people were"} handed to the dialler at{" "}
          {outOfWindow.map((r) => hourLabel(r.hr)).join(", ")} today — the window for them opens at{" "}
          {outOfWindow[0].window_open?.slice(0, 5)} and closes at{" "}
          {outOfWindow[0].window_close?.slice(0, 5)} Eastern. This should not be possible; check the
          engine's window gate.
        </div>
      )}

      {/* The banner and the "Needs attention" tile read the SAME number from the same source, so
          one can never claim trouble while the other shows none. */}
      {problemCount > 0 && (
        <div className="banner bad" style={{ marginTop: 10 }}>
          <b>Needs a look:</b> {fmt.n(problemCount)} {problemCount === 1 ? "person" : "people"} may
          still be in a Five9 list after a failed removal, which means Five9 could call them again
          {unremoved.oldest_at ? <> — the oldest since {fmt.et(unremoved.oldest_at)}</> : null}. This
          does not clear itself; somebody has to check Five9.
        </div>
      )}

      {/* A stopped campaign with people waiting is the failure that hid for a whole day. It gets
          the top of the page, not just a row in a panel further down. */}
      {(f9?.lists ?? []).some((r) => r.stalled) && (
        <div className="banner bad" style={{ marginTop: 10 }}>
          <b>Five9 will not ring these{f9?.error ? " (from a stale read)" : ""}:</b>{" "}
          {f9!.lists
            .filter((r) => r.stalled)
            .map((r) => `${r.list} (${fmt.n(r.records)} waiting, ${r.campaign} is ${r.state})`)
            .join("; ")}
          . Start the campaign in Five9, or those people sit there uncalled.
        </div>
      )}

      <div className="tiles" style={{ marginTop: 14 }}>
        {/* Green only when there is something to be green ABOUT. A zero in a green tile with a
            green stripe reads as "0 calls, and that is good". */}
        <div className={`tile${!dialsUnreadable && d.confirmed_dials > 0 ? " ok" : ""}`}>
          <span className="lab">Calls made today</span>
          <span className="v">{n(d.confirmed_dials, dialsUnreadable)}</span>
          <span className="d">Five9 rang the phone and told us how it went</span>
        </div>
        {/* In TEST MODE the engine still writes `added_at`, so this counts records Five9 never
            saw - and the Five9 adds/removes panel below correctly shows 0 for the same day. Two
            panels flatly contradicting each other unless this one says which it is. */}
        <div className="tile">
          <span className="lab">
            {s.dry_run ? "Would have been sent (test mode)" : "Sent to the dialler today"}
          </span>
          <span className="v">{n(d.handed_to_five9, dialsUnreadable)}</span>
          <span className="d">
            {s.dry_run
              ? "test mode — Five9 was never told about any of these"
              : "Five9 only rings someone when an agent is free, so this is always higher than calls made"}
          </span>
        </div>
        <div className="tile">
          <span className="lab">Still to call today</span>
          <span className="v">{n(toGoToday, queueUnreadable)}</span>
          <span className="d">
            {/* `oldest_due_at` rather than folding the late ones into "next": once a backlog
                starts draining, `min(scheduled_for)` over everything scheduled is a time in the
                PAST, and labelling that "next" is simply false. And the queue total no longer
                asserts "most of it future appointments", which was never measured. */}
            {q.oldest_due_at ? (
              <>
                <b>{fmt.et(q.oldest_due_at)} ET</b> is the longest anyone has been waiting ·{" "}
              </>
            ) : q.next_at ? (
              <>next {fmt.et(q.next_at)} ET · </>
            ) : null}
            {n(q.scheduled, queueUnreadable)} in the queue altogether, today and beyond
          </span>
        </div>
        {/*
          TWO TILES, not one number. These measure different things in different units - "may still
          be in a list" and "waited too long" - and a single tile had to either add them (a number
          meaning nothing) or show one of them (`problemCount || overdue` printed 3 while its own
          sub-line read "3 … · 40 …").

          No `.tile.wa` exists in the stylesheet - only `.ok` and `.ba` - so an amber variant
          renders as a plain tile indistinguishable from its neighbour.
        */}
        <div className={`tile${problemCount > 0 ? " ba" : ""}`}>
          <span className="lab">Maybe still in a list</span>
          <span className="v">{n(problemCount, (t.removal_unverified ?? []).length === 0)}</span>
          <span className="d">
            {problemCount === 0
              ? "every removal was confirmed by Five9"
              : "Five9 did not confirm the removal, so these could be called again"}
          </span>
        </div>
        <div className={`tile${overdue > 0 ? " ba" : ""}`}>
          <span className="lab">Waited too long</span>
          <span className="v">{t.overdue_hours == null ? "—" : fmt.n(overdue)}</span>
          <span className="d">
            {/* The cutoff is nullable, and when it is null NOTHING is ever dropped - so a "0" here
                was not "all clear", it was "the question does not apply", twelve lines above a red
                "overdue" row in the horizon table. */}
            {t.overdue_hours == null
              ? "no staleness cutoff is set, so nobody is ever dropped for waiting"
              : overdue > 0
                ? `waited past the ${t.overdue_hours}h cutoff and will be dropped`
                : `nobody has waited past the ${t.overdue_hours}h cutoff`}
          </span>
        </div>
      </div>

      {/* Only when somebody IS in there. Empty, this card said "Nobody — and that is the healthy
          state", which is four lines of screen explaining that nothing is wrong. */}
      {detail.length > 0 && (
        //: Titled from `q.in_flight`, not the row count: the query is `limit 50`, so with 60 in
        //: flight the headline said 60 and this card said 50.
        <Card title={`In the dialler right now — ${fmt.n(inList)}`}>
          {detail.length < inList && (
            <p className="dim" style={{ marginTop: -6, fontSize: 12 }}>
              Showing the first {fmt.n(detail.length)} of {fmt.n(inList)}.
            </p>
          )}
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Phone</th>
                  <th>Which list</th>
                  <th>Why we are calling</th>
                  <th className="mono">Step</th>
                  <th>State</th>
                  <th className="mono">Added</th>
                </tr>
              </thead>
              <tbody>
                {/* `r.id`: one person can hold two plans in two lists at the same `dial_no`, which
                    a (phone, dial_no) key collides on - a real state in this estate. */}
                {detail.map((r) => (
                  <tr key={r.id}>
                    <td className="mono">{r.phone_last10}</td>
                    {/* Which of the four shared lists somebody is in is the operational question
                        behind a double-dial, and it was being returned and never shown. */}
                    <td className="mono" style={{ fontSize: 12 }}>
                      {r.list_name}
                    </td>
                    <td>{WHY_CALLING[r.entry_type] ?? r.entry_type}</td>
                    <td className="mono">{ordinal(r.dial_no)}</td>
                    <td>
                      {r.status === "added" ? (
                        <Pill tone="ok">in Five9</Pill>
                      ) : (
                        /* 'claimed' is NOT in the dialler: a worker has locked the row and has not
                           called Five9 yet, which is why `added_at` is null for these. */
                        <Pill tone="nu">being handed over</Pill>
                      )}
                    </td>
                    <td className="mono">{r.added_at ? fmt.et(r.added_at) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* What FIVE9 says, which is the half our own database cannot know. In the "is it working"
          section rather than further down because a stopped campaign is a live outage: every
          redial-day dial routes to `Vaunt`, and only `Vaunt_Outbound` dials it. */}
      <Card
        title="What Five9 says right now"
        actions={
          f9 ? (
            <span className="dim" style={{ fontSize: 11 }}>
              read from Five9 {f9.age_seconds}s ago
            </span>
          ) : null
        }
      >
        <p className="dim" style={{ marginTop: -6, fontSize: 13 }}>
          Our own records above say what we <i>think</i>. This is Five9's answer: how many people
          are sitting in each calling list, and whether the campaign that dials it is running. A
          list with people in it and a stopped campaign means those phones will not ring.
        </p>
        {/* A STALE read keeps its last good rows (`five9_state.cached` attaches them on purpose, so
            the page keeps rendering). Discarding them meant the panel said "unknown" while the red
            banner at the top of the page was still computed from those very rows - the two
            contradicting each other. Now the warning sits ABOVE the table and the table still
            shows, with its age. */}
        {f9?.error && (
          <div className="banner bad">
            <b>Could not read Five9 just now:</b> {f9.error}.{" "}
            {f9.lists.length > 0
              ? `Showing the last successful read, ${f9.age_seconds}s old.`
              : "No earlier read to fall back on — these numbers are unknown, not zero."}
          </div>
        )}
        {!f9 ? (
          <div className="empty">Asking Five9…</div>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>List</th>
                  <th>What it is for</th>
                  <th className="mono">People in it</th>
                  <th>Campaign</th>
                </tr>
              </thead>
              <tbody>
                {f9.lists.map((r) => (
                  <tr key={r.list}>
                    <td className="mono">{r.list}</td>
                    <td className="dim" style={{ fontSize: 12 }}>
                      {r.purpose}
                    </td>
                    <td className="mono">{fmt.n(r.records)}</td>
                    <td>
                      {r.state_unreadable ? (
                        /* A SOAP timeout, not a stopped campaign. Telling the owner to "start the
                           campaign in Five9" because a read failed is the same unknown-vs-off
                           mistake this page makes a point of not making about its own switches. */
                        <Pill tone="wa">could not read the campaign</Pill>
                      ) : r.stalled ? (
                        <Pill tone="ba">{r.state} — these will not ring</Pill>
                      ) : r.state === "RUNNING" ? (
                        <Pill tone="ok">running</Pill>
                      ) : (
                        /* Stopped with nobody in it is not a problem - there is nothing waiting
                           to ring. Flagging it red would train somebody to ignore red. */
                        <Pill tone="nu">{r.state.toLowerCase()} — nobody waiting</Pill>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ════════════ 2. WHAT HAPPENS NEXT ════════════ */}
      <Card title="Today, hour by hour">
        {/* The window comes from `t.policies`, which is editable data - it was hardcoded as
            "11:00am" here and in the subtitle, so an edited policy made the page quietly wrong. */}
        <p className="dim" style={{ marginTop: -6, fontSize: 13 }}>
          {windowText
            ? `Calling opens at ${windowText.open} ET and closes at ${windowText.close}, so the first hour is usually the busiest — everything that came due overnight goes out then. `
            : "Everything that came due overnight goes out when calling opens, so the first hour is usually the busiest. "}
          {switchesUnreadable
            ? "How many sit in Five9 at once could not be read."
            : limitPhrase(s.max_in_flight_redials)}
        </p>
        {todayRows.length === 0 ? (
          <div className="empty">Nothing is scheduled for today and nothing has gone out yet.</div>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Hour handed over (ET)</th>
                  <th className="mono">Went out</th>
                  {/* NOT "rang in this hour". Both columns bucket on `added_at`, so a record handed
                      over at 11:00 and dialled at 15:00 counts in the 11am row - which is the right
                      grouping for "did what we sent at 11 get called", and the wrong reading of a
                      bare "Rang" under a column headed "Hour". */}
                  <th className="mono">…of which rang</th>
                  <th className="mono">Still to go</th>
                  <th style={{ width: "40%" }} />
                </tr>
              </thead>
              <tbody>
                {todayRows.map((h) => {
                  //: An hour the engine was not allowed to dial in. Red, and named - not a green
                  //: bar the reader has to decode by checking the clock against a window they are
                  //: expected to remember.
                  const bad = outOfWindow.some((o) => o.hr === h.hr);
                  return (
                  <tr key={h.hr}>
                    <td className="mono" style={bad ? { color: "var(--bad)" } : undefined}>
                      {hourLabel(h.hr)}
                      {bad && (
                        <>
                          {" "}
                          <Pill tone="ba">outside the allowed hours</Pill>
                        </>
                      )}
                    </td>
                    <td className="mono">{h.went_out ? fmt.n(h.went_out) : <span className="dim">—</span>}</td>
                    <td className="mono">{h.rang ? fmt.n(h.rang) : <span className="dim">—</span>}</td>
                    <td className="mono">{h.to_go ? fmt.n(h.to_go) : <span className="dim">—</span>}</td>
                    <td>
                      <Bar
                        value={h.to_go + h.went_out}
                        peak={peakHour}
                        tone={bad ? "var(--bad)" : h.to_go > 0 ? "var(--acc)" : "var(--ok)"}
                      />
                    </td>
                  </tr>
                  );
                })}
                <tr>
                  <td>
                    <b>Today</b>
                  </td>
                  <td className="mono">
                    <b>{fmt.n(wentToday)}</b>
                  </td>
                  <td className="mono">
                    <b>{fmt.n(todayRows.reduce((a, h) => a + h.rang, 0))}</b>
                  </td>
                  <td className="mono">
                    <b>{n(toGoToday, queueUnreadable)}</b>
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <p style={{ marginTop: 16, marginBottom: 2, fontSize: 13 }}>
          <b>Beyond today</b>
        </p>
        {/* "Most of it is future appointments" was ASSERTED, not measured, and read as nonsense at
            zero ("Most of the 0 waiting is not today"). Now it states what the buckets say. */}
        <p className="dim" style={{ marginTop: 0, fontSize: 12 }}>
          {q.scheduled
            ? `${fmt.n(q.scheduled)} people are waiting in total; an appointment call is scheduled for the appointment itself, which can be months out.`
            : "Nobody is waiting."}
        </p>
        {(t.schedule_horizon ?? []).length === 0 ? (
          <div className="empty">Nothing is scheduled at all.</div>
        ) : (
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th className="mono">Attempts</th>
                <th>First one</th>
                <th>Last one</th>
              </tr>
            </thead>
            <tbody>
              {(t.schedule_horizon ?? []).map((h) => (
                <tr key={h.bucket}>
                  <td>
                    {/* "today" here means today AND STILL AHEAD - anything already due is counted
                        in `overdue` instead - which read as a third, contradictory "today". */}
                    {h.bucket === "overdue" ? (
                      <Pill tone="ba">already due, not yet called</Pill>
                    ) : h.bucket === "today" ? (
                      "today, still ahead"
                    ) : (
                      h.bucket
                    )}
                  </td>
                  <td className="mono">{fmt.n(h.n)}</td>
                  <td className="dim">{fmt.et(h.first_at)}</td>
                  <td className="dim">{fmt.et(h.last_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

        {(t.queue_by_entry ?? []).length > 0 && (
          <>
            <p style={{ marginTop: 16, marginBottom: 2, fontSize: 13 }}>
              <b>Why those people are queued</b>
            </p>
            <div className="tw">
              <table>
                <thead>
                  <tr>
                    <th>Reason we are calling</th>
                    <th className="mono">Waiting</th>
                    <th className="mono">Due now</th>
                  </tr>
                </thead>
                <tbody>
                  {t.queue_by_entry.map((r) => (
                    <tr key={r.entry_type}>
                      <td>{WHY_CALLING[r.entry_type] ?? r.entry_type}</td>
                      <td className="mono">{fmt.n(r.n)}</td>
                      <td className="mono">{fmt.n(r.due_now)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      {/* ════════════ 3. WHAT HAPPENED ════════════ */}
      <div className="row w" style={{ marginTop: 22, marginBottom: 2, gap: 6 }}>
        <h2 style={{ fontSize: 19 }}>What happened before</h2>
        <span className="sp" />
        {RANGES.map((r) => (
          <button
            key={r.days}
            className={`btn s${!custom && days === r.days ? " p" : ""}`}
            onClick={() => {
              setCustom(false);
              setDays(r.days);
            }}
          >
            {r.label}
          </button>
        ))}
        <button
          className={`btn s${custom ? " p" : ""}`}
          onClick={() => setCustom(true)}
          title="Any two dates, eastern time, both included"
        >
          Dates…
        </button>
        {busy && (
          <span className="dim" style={{ fontSize: 11 }}>
            loading…
          </span>
        )}
        {custom && (
          <>
            <input
              className="f"
              style={{ fontSize: 12 }}
              type="date"
              value={from}
              max={etDay()}
              onChange={(e) => setFrom(e.target.value)}
              aria-label="From"
            />
            <span className="dim">to</span>
            <input
              className="f"
              style={{ fontSize: 12 }}
              type="date"
              value={to}
              max={etDay()}
              onChange={(e) => setTo(e.target.value)}
              aria-label="To"
            />
          </>
        )}
      </div>
      <p className="dim" style={{ marginTop: 0, fontSize: 12 }}>
        Everything below covers <b>{rangeLabel}</b>, Eastern time, both days included. Every panel
        in this section uses that one range.
      </p>

      <Card title={`Calls by day — ${rangeLabel}`}>
        <p className="dim" style={{ marginTop: -6, fontSize: 13 }}>
          "Rang" is the only honest count of a call: Five9 gave us a matching call record. "Sent to
          the dialler" is what we handed over, which is always higher. "Kept going" and "Stopped"
          will not add up to "Rang" — a dial whose outcome Five9 has not reported yet is in neither.
        </p>
        {(t.dials_by_day ?? []).length === 0 ? (
          <div className="empty">No calls were handed to Five9 in this period.</div>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Day</th>
                  <th className="mono">Rang</th>
                  <th className="mono">Sent to the dialler</th>
                  <th className="mono">People</th>
                  <th className="mono">Kept going</th>
                  <th className="mono">Stopped</th>
                  <th style={{ width: "22%" }} />
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const peak = Math.max(1, ...t.dials_by_day.map((r) => r.handed_over));
                  return t.dials_by_day.map((r) => (
                    <tr key={String(r.day)}>
                      <td>{dayLabel(r.day)}</td>
                      <td className="mono">{fmt.n(r.confirmed)}</td>
                      <td className="mono">{fmt.n(r.handed_over)}</td>
                      <td className="mono">{fmt.n(r.people)}</td>
                      <td className="mono">{fmt.n(r.continued)}</td>
                      <td className="mono">{fmt.n(r.ended)}</td>
                      <td>
                        <Bar value={r.handed_over} peak={peak} tone="var(--acc)" />
                      </td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/*
        Ammar's literal question, 2026-10-06: "how many dialed and added by texterai and how many by
        appointments automation". The card below it CANNOT answer that - it reads `redial_plans`,
        which is only this engine's own cadences. TextersAI, the appointment automation and the
        tracker add straight to the same Five9 lists and never create a plan of ours, so the only
        place every writer appears is `list_ledger.actor_system`. The backend was already computing
        it and the page never rendered it.
      */}
      <Card title={`Who wrote to the Five9 lists — ${rangeLabel}`}>
        {/*
          THE CAPTION HERE WAS THE WORST THING ON THE PAGE. It said "Every system that touched a
          calling list, including the ones that bypass this engine entirely" - and that is false.
          This reads `list_ledger`, which only OUR OWN code writes. TextersAI and the appointment
          automation place their adds straight into Five9 and never write a row, and they own the
          FIRST dial (`engine_owns_first_dial` is false), which is exactly what was being asked
          about. An absence of TextersAI here is not evidence TextersAI added nobody.

          So the caption now says what it can and cannot see, and points at the one check that does
          not depend on another system's cooperation: Five9's own list size against our own count.
        */}
        <p className="dim" style={{ marginTop: -6, fontSize: 13 }}>
          Only systems that record what they do in our own ledger — this engine and its sweeps.
          <b> TextersAI and the appointment automation are not here:</b> they add straight into Five9
          and do not report it, so compare “What Five9 says” above against these numbers to see their
          adds. An unfamiliar name below means something else is writing to our lists.
        </p>
        {(t.writers_five9 ?? []).length === 0 ? (
          <div className="empty">No list was touched by anything in this period.</div>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>System</th>
                  <th className="mono">Added</th>
                  <th className="mono">People added</th>
                  <th className="mono">Removed</th>
                  <th className="mono">Lists</th>
                  <th>Last touched</th>
                </tr>
              </thead>
              <tbody>
                {t.writers_five9.map((w) => (
                  <tr key={w.actor_system}>
                    <td>
                      <b>{SYSTEM_NAMES[w.actor_system] ?? w.actor_system}</b>
                      {!SYSTEM_NAMES[w.actor_system] && (
                        <>
                          {" "}
                          <Pill tone="wa">unrecognised</Pill>
                        </>
                      )}
                    </td>
                    <td className="mono">{fmt.n(w.adds)}</td>
                    <td className="mono">{fmt.n(w.people_added)}</td>
                    <td className="mono">{fmt.n(w.removes)}</td>
                    <td className="mono">{fmt.n(w.lists_touched)}</td>
                    <td className="dim">{fmt.et(w.last_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title={`Which of our own cadences they came from — ${rangeLabel}`}>
        <p className="dim" style={{ marginTop: -6, fontSize: 13 }}>
          This engine's own calling sequences only — why each person was put on one.
        </p>
        {(t.writers_window ?? []).length === 0 ? (
          <div className="empty">Nobody was added to a calling queue in this period.</div>
        ) : (
          <div className="tw">
            <table>
              <thead>
                {/* No "Today" column. It read `writers_today`, measured from Eastern midnight,
                    directly under a sentence promising every panel here obeys the chosen range -
                    so picking Sep 1-5 showed today's numbers in the middle of it. Today is section
                    one's job. */}
                <tr>
                  <th>Why they were queued</th>
                  <th className="mono">People put on it</th>
                  <th className="mono">Still being called</th>
                </tr>
              </thead>
              <tbody>
                {t.writers_window.map((w) => (
                  <tr key={`${w.reason}\u0000${w.created_by}`}>
                    <td>
                      <b>{WHO_ASKED[w.reason] ?? w.reason}</b>
                      <div className="dim" style={{ fontSize: 11 }}>
                        {w.created_by}
                      </div>
                    </td>
                    <td className="mono">{fmt.n(w.plans)}</td>
                    <td className="mono">{fmt.n(w.still_active)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title={`Why we did not call someone — ${rangeLabel}`}>
        {/* "People" was wrong on this table: it counts ATTEMPTS, and one person can hold two plans
            in two lists. And `last_error` holds only the LATEST reason, so an attempt held five
            times for five different reasons appears once - this is not a tally of holds. */}
        <p className="dim" style={{ marginTop: -6, fontSize: 13 }}>
          Most of these are the engine protecting somebody on purpose. Green is good, red needs
          checking. Counted per attempt, by its most recent reason — calls that simply went through
          are not here.
        </p>
        {holdRows.length === 0 ? (
          <div className="empty">Nobody was held back in this period.</div>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Reason</th>
                  {/* Attempts, not people: `count(*)` over `redial_attempts`, and one person can
                      hold two plans in two lists at once. */}
                  <th className="mono">Attempts</th>
                </tr>
              </thead>
              <tbody>
                {holdRows.map((h) => (
                  <tr key={h.label}>
                    <td>
                      <Pill tone={h.tone}>{h.label}</Pill>
                    </td>
                    <td className="mono">{fmt.n(h.n)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="g2">
        <Card title={`Adding and removing in Five9 — ${rangeLabel}`}>
          {/* The old caption said adds and removes "should roughly match". They should NOT: one
              logical "take this lead out" deletes from EVERY managed list, so several removes per
              add is correct, and a caption promising otherwise teaches people to ignore the panel
              or raise a false alarm. */}
          {/* Was `f9?.lists.length ?? 5`, wrong in BOTH branches: an unreadable Five9 gives
              `lists: []`, so it read "all 0 lists at once", and a readable one gave 5 because the
              Five9 panel pairs five lists while only FOUR are removal targets
              (`connected_sweep.MANAGED_LISTS`). No number at all beats a confident wrong one. */}
          <p className="dim" style={{ marginTop: -6, fontSize: 13 }}>
            Removes run higher than adds by design — taking somebody out deletes them from every
            calling list at once, so one removal is several operations.
          </p>
          {(t.list_by_day ?? []).length === 0 ? (
            <div className="empty">Nothing was added or removed in this period.</div>
          ) : (
            <div className="tw">
              <table>
                <thead>
                  <tr>
                    <th>Day</th>
                    <th className="mono">Added</th>
                    <th className="mono">Removed</th>
                    <th className="mono">…because we spoke</th>
                  </tr>
                </thead>
                <tbody>
                  {t.list_by_day.map((r) => (
                    <tr key={String(r.day)}>
                      <td>{dayLabel(r.day)}</td>
                      <td className="mono">{fmt.n(r.adds)}</td>
                      <td className="mono">{fmt.n(r.removes)}</td>
                      <td className="mono dim">{fmt.n(r.sweep_removes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title={`How far people get — ${rangeLabel}`}>
          {/* "up to six tries" was false and the table below it proved so: the cadence is six
              attempts on the FIRST day and then once a day after that, so `dial_no` runs well past
              six and contradicted its own caption. */}
          <p className="dim" style={{ marginTop: -6, fontSize: 13 }}>
            Somebody who asks for a call gets six tries on the first day, then once a day after
            that. This is how many people reach each attempt.
          </p>
          {(t.cadence_funnel ?? []).length === 0 ? (
            <div className="empty">No calls in this period.</div>
          ) : (
            <div className="tw">
              <table>
                <thead>
                  <tr>
                    <th>Attempt</th>
                    <th className="mono">Rang</th>
                    <th className="mono">Kept going</th>
                    <th className="mono">Stopped here</th>
                  </tr>
                </thead>
                <tbody>
                  {t.cadence_funnel.map((r) => (
                    <tr key={r.dial_no}>
                      <td>{ordinal(r.dial_no)}</td>
                      <td className="mono">{fmt.n(r.dialled)}</td>
                      <td className="mono">{fmt.n(r.continued)}</td>
                      <td className="mono">{fmt.n(r.ended)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Card title="Settings">
        <div className="tw">
          <table>
            <tbody>
              <tr>
                <td>Engine</td>
                <td>
                  <Pill tone={on ? "ok" : "ba"}>{on ? "ON — making calls" : "not calling"}</Pill>
                </td>
              </tr>
              {/* "no limit" is the right word for a NULL that was really read. When the settings
                  query FAILED, `s` is {} and every one of these asserted "no limit" and a red "not
                  calling" about an engine that might be dialling - the same unknown-as-fact bug the
                  headline was fixed for, two cards lower down. */}
              <tr>
                <td>Most calls allowed in one day</td>
                <td className="mono">
                  {switchesUnreadable ? "unknown" : s.daily_cap == null ? "no limit" : fmt.n(s.daily_cap)}
                </td>
              </tr>
              <tr>
                <td>Most people queued in Five9 at once</td>
                <td className="mono">
                  {switchesUnreadable ? "unknown" : (s.max_in_flight_redials ?? "no limit")}
                </td>
              </tr>
              <tr>
                <td>Dropped if they wait longer than</td>
                <td className="mono">
                  {t.overdue_hours == null ? "never dropped" : `${t.overdue_hours} hours`}
                </td>
              </tr>
              <tr>
                <td>Engine last ran</td>
                <td className="dim">{fmt.et(s.last_tick_at)} ET — {s.last_tick_summary ?? "—"}</td>
              </tr>
              <tr>
                <td>Last call placed</td>
                <td className="dim">{fmt.et(s.last_dial_at)} ET</td>
              </tr>
              <tr>
                <td>Settings last changed</td>
                <td className="dim">
                  {fmt.et(s.updated_at)} ET — {s.updated_by ?? "—"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
